import "../../components/EventUserDashboard.css";

import React, { useEffect, useState } from "react";
import { Select } from "@mantine/core";

/* MATCH BACKEND ENUM */
enum Bar {
    BAR1 = 1,
    BAR2 = 2,
    BAR3 = 3
}

interface EventDto {
    eventID: number;
    eventName?: string;
}

interface FunctionDto {
    functionId: number;
    functionName: string;
}

interface FunctionAllowanceDto {
    bar: number;
    maxDrinks: number;
}

const getBarName = (bar: number) => {
    switch (bar) {
        case Bar.BAR1:
            return "VIP Lounge";
        case Bar.BAR2:
            return "Hygge Lounge";
        case Bar.BAR3:
            return "Jail House Bar";
        default:
            return "Ukendt";
    }
};

const toArray = <T,>(result: any): T[] => {
    if (Array.isArray(result)) return result;
    if (Array.isArray(result?.data)) return result.data;
    if (Array.isArray(result?.items)) return result.items;
    if (Array.isArray(result?.functions)) return result.functions;
    if (Array.isArray(result?.results)) return result.results;
    return [];
};

const toNonNegativeNumber = (value: string | number): number => {
    const num = Number(value);
    if (!Number.isFinite(num) || num < 0) return 0;
    return Math.floor(num);
};

const AdminDrinkFunctionAllowance: React.FC = () => {
    const [events, setEvents] = useState<EventDto[]>([]);
    const [functions, setFunctions] = useState<FunctionDto[]>([]);

    const [eventId, setEventId] = useState<number>(0);
    const [eventName, setEventName] = useState<string>("");

    const [functionId, setFunctionId] = useState<number>(0);
    const [functionName, setFunctionName] = useState<string>("");

    const [bar1Drinks, setBar1Drinks] = useState<number>(0);
    const [bar2Drinks, setBar2Drinks] = useState<number>(0);
    const [bar3Drinks, setBar3Drinks] = useState<number>(0);

    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const resetBars = () => {
        setBar1Drinks(0);
        setBar2Drinks(0);
        setBar3Drinks(0);
    };

    useEffect(() => {
        const loadEvents = async () => {
            try {
                const res = await fetch("/api/events");
                if (!res.ok) throw new Error(`Kunne ikke hente events (${res.status})`);

                const result = await res.json();
                setEvents(toArray<EventDto>(result));
            } catch (err) {
                console.error(err);
                setError("Kunne ikke hente events");
            }
        };

        loadEvents();
    }, []);

    const handleEventChange = async (id: number) => {
        setEventId(id);
        setFunctionId(0);
        setFunctionName("");
        setFunctions([]);
        resetBars();
        setMessage(null);
        setError(null);

        if (!id) {
            setEventName("");
            return;
        }

        const selected = events.find((e) => e.eventID === id);
        setEventName(selected?.eventName || `Event ${id}`);

        try {
            const res = await fetch(`/api/events/${id}`);
            if (!res.ok) throw new Error(`Kunne ikke hente event (${res.status})`);

            const result = await res.json();
            const data = toArray<FunctionDto>(result?.functions ?? result);
            setFunctions(data);
        } catch (err) {
            console.error(err);
            setError("Kunne ikke hente functions");
            setFunctions([]);
        }
    };

    const loadFunctionAllowances = async (id: number, keepMessage = false) => {
        resetBars();

        if (!keepMessage) {
            setMessage(null);
        }

        setError(null);

        const selected = functions.find((f) => f.functionId === id);
        setFunctionName(selected?.functionName || "");

        if (!id || !eventId) return;

        try {
            const res = await fetch(
                `/api/admin/drinks-function?functionId=${id}&eventId=${eventId}`
            );

            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || `Kunne ikke hente drinks (${res.status})`);
            }

            const result = await res.json();
            const data = toArray<FunctionAllowanceDto>(result);

            data.forEach((item) => {
                if (item.bar === Bar.BAR1) setBar1Drinks(toNonNegativeNumber(item.maxDrinks));
                if (item.bar === Bar.BAR2) setBar2Drinks(toNonNegativeNumber(item.maxDrinks));
                if (item.bar === Bar.BAR3) setBar3Drinks(toNonNegativeNumber(item.maxDrinks));
            });
        } catch (err: any) {
            console.error(err);
            setError(err?.message || "Kunne ikke hente drinks");
        }
    };

    const handleFunctionChange = async (id: number) => {
        setFunctionId(id);
        await loadFunctionAllowances(id, false);
    };

    const submit = async () => {
        if (!eventId || !functionId) {
            setError("Event og Function skal vælges");
            return;
        }

        setMessage(null);
        setError(null);
        setIsSaving(true);

        const bars = [
            { bar: Bar.BAR1, maxDrinks: toNonNegativeNumber(bar1Drinks) },
            { bar: Bar.BAR2, maxDrinks: toNonNegativeNumber(bar2Drinks) },
            { bar: Bar.BAR3, maxDrinks: toNonNegativeNumber(bar3Drinks) }
        ];

        try {
            for (const b of bars) {
                const res = await fetch("/api/admin/drinks-function", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        functionId,
                        eventId,
                        bar: b.bar,
                        maxDrinks: b.maxDrinks
                    })
                });

                if (!res.ok) {
                    const text = await res.text();
                    throw new Error(
                        text || `Fejl ved gemning for ${getBarName(b.bar)} (${res.status})`
                    );
                }
            }

            await loadFunctionAllowances(functionId, true);
            setMessage("Drinks gemt for alle barer");
        } catch (err: any) {
            console.error(err);
            setError(err?.message || "Fejl ved gemning");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="dashContainer">
            <div className="wrapper">
                <div className="top">
                    <div className="name">
                        {eventName || "Vælg event"}
                    </div>
                    <div className="meta">
                        {functionName
                            ? `${functionName} – Juster drinks pr. bar`
                            : "Vælg event og function"}
                    </div>
                </div>

                <div className="content">
                    {error && (
                        <div className="badgeRed" style={{ padding: 8, marginBottom: 16 }}>
                            {error}
                        </div>
                    )}

                    {message && (
                        <div className="statusBadge" style={{ padding: 8, marginBottom: 16 }}>
                            {message}
                        </div>
                    )}

                    <div className="sectionTitle">Valg</div>

                    <div className="statsGrid3" style={{ marginTop: 12 }}>
                        <div className="stat" style={{ padding: 16 }}>
                            <Select
                                placeholder="Vælg event"
                                value={eventId ? String(eventId) : null}
                                onChange={(val) => handleEventChange(val ? Number(val) : 0)}
                                data={events.map((e) => ({
                                    value: String(e.eventID),
                                    label: e.eventName || `Event ${e.eventID}`
                                }))}
                                clearable
                            />
                        </div>

                        {eventId > 0 && (
                            <div className="stat" style={{ padding: 16 }}>
                                <Select
                                    placeholder="Vælg function"
                                    value={functionId ? String(functionId) : null}
                                    onChange={(val) => handleFunctionChange(val ? Number(val) : 0)}
                                    data={functions.map((f) => ({
                                        value: String(f.functionId),
                                        label: f.functionName
                                    }))}
                                    clearable
                                />
                            </div>
                        )}
                    </div>

                    {functionId > 0 && (
                        <>
                            <div className="sectionTitle" style={{ marginTop: 30 }}>
                                Drinks pr. bar
                            </div>

                            <div className="statsGrid3" style={{ marginTop: 12 }}>
                                <div className="stat" style={{ padding: 16 }}>
                                    <label>{getBarName(Bar.BAR1)}</label>
                                    <input
                                        type="number"
                                        min={0}
                                        value={bar1Drinks}
                                        onChange={(e) => setBar1Drinks(toNonNegativeNumber(e.target.value))}
                                    />
                                </div>

                                <div className="stat" style={{ padding: 16 }}>
                                    <label>{getBarName(Bar.BAR2)}</label>
                                    <input
                                        type="number"
                                        min={0}
                                        value={bar2Drinks}
                                        onChange={(e) => setBar2Drinks(toNonNegativeNumber(e.target.value))}
                                    />
                                </div>

                                <div className="stat" style={{ padding: 16 }}>
                                    <label>{getBarName(Bar.BAR3)}</label>
                                    <input
                                        type="number"
                                        min={0}
                                        value={bar3Drinks}
                                        onChange={(e) => setBar3Drinks(toNonNegativeNumber(e.target.value))}
                                    />
                                </div>
                            </div>

                            <button
                                type="button"
                                style={{
                                    marginTop: 20,
                                    width: "100%",
                                    opacity: isSaving ? 0.7 : 1,
                                    cursor: isSaving ? "not-allowed" : "pointer"
                                }}
                                onClick={submit}
                                disabled={isSaving}
                            >
                                {isSaving ? "Gemmer..." : "Gem ændringer"}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminDrinkFunctionAllowance;